import type { ReactNode } from "react";
import { isValidElement } from "react";
import type { AccordionItem } from "@/components/site/Accordion";
import { HOME_FAQ } from "@/components/site/faq-data";
import { PHONE_HREF } from "@/components/site/primitives";

function homeFaq(index: number): AccordionItem {
  const item = HOME_FAQ[index];
  if (!item) throw new Error(`Missing homepage FAQ at ${index}`);
  return item;
}

export const FAQ_GROUPS: { label: string; items: AccordionItem[] }[] = [
  {
    label: "Choosing Insulation",
    items: [
      homeFaq(0),
      homeFaq(1),
      {
        q: "What's the difference between open-cell and closed-cell spray foam?",
        a: "Both are spray foam. One is denser and stronger at stopping air and moisture; the other is lighter and fills odd spaces well. Gary will tell you which one earns its cost in your building — we don't push a product that doesn't fit.",
      },
    ],
  },
  {
    label: "Pricing & Quotes",
    items: [
      homeFaq(2),
      {
        q: "Does the meet-or-beat offer cover blown or batting too?",
        a: "No. We'll meet or beat any spray foam bid with proof of bid — spray foam services only. Blown and batting are quoted fairly and up front.",
      },
      homeFaq(5),
    ],
  },
  {
    label: "The Work",
    items: [
      homeFaq(3),
      {
        q: "How long does a typical install take?",
        a: "It depends on the size of the building and the product. When we quote, we'll give you a clear window so you can plan around it — especially on new construction when framing and drywall are waiting.",
      },
      {
        q: "What should we expect on the job site?",
        a: "Careful work and a tidy site. We show up, do the job, and clean up when we're done. You shouldn't have to chase a mess after we leave.",
      },
    ],
  },
  {
    label: "Coverage",
    items: [
      homeFaq(4),
      {
        q: "We're not sure we're close enough. Should we still call?",
        a: (
          <>
            Yes. We&rsquo;re based in Prairie Farm, WI and serve Northwest Wisconsin. If
            you&rsquo;re not sure whether you&rsquo;re in range,{" "}
            <a href={PHONE_HREF} className="font-bold text-foam-deep">
              call
            </a>{" "}
            — we&rsquo;ll tell you straight.
          </>
        ),
      },
    ],
  },
];

function toPlainText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join("");
  if (isValidElement(node)) {
    return toPlainText((node.props as { children?: ReactNode }).children);
  }
  return "";
}

export function faqPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_GROUPS.flatMap((group) =>
      group.items.map((item) => ({
        "@type": "Question" as const,
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer" as const,
          text: toPlainText(item.a),
        },
      })),
    ),
  };
}
